import { useEffect, useState } from "react";
import {
    adminFeedbackList,
    adminFeedbackPatch,
    adminFeedbackConvertTicket,
    adminFeedbackTopThemes,
} from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, MessageSquareWarning, Star, Filter, ExternalLink, Award, TrendingUp } from "lucide-react";
import FeedbackTriagePanel from "./FeedbackTriagePanel";

const SOURCE_LABEL = {
    review: "Review",
    report_feedback: "Report",
    issue_feedback: "Issue",
    exposure_feedback: "Exposure",
    feature_request: "Feature request",
};

const STATUS_TONE = {
    new: "bg-blue-100 text-blue-700",
    reviewed: "bg-emerald-100 text-emerald-700",
    archived: "bg-bl-bg text-bl-muted",
    testimonial_candidate: "bg-bl-teal-tint text-bl-primary",
    issue_flagged: "bg-[#C85B3A]/15 text-[#C85B3A]",
    support_ticket_created: "bg-amber-100 text-amber-800",
    calibration_candidate: "bg-bl-teal-tint text-bl-primary",
    feature_request: "bg-blue-100 text-blue-700",
};

function StatItem({ label, value, tone = "text-bl" }) {
    return (
        <div className="text-center">
            <div className={`font-display font-semibold text-2xl ${tone}`}>
                {value}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                {label}
            </div>
        </div>
    );
}

function TopThemesPanel() {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const refresh = async (d = days) => {
        setLoading(true);
        try {
            const r = await adminFeedbackTopThemes(d, 8);
            setData(r);
        } catch (e) {
            // Best-effort widget — non-fatal.
            setData({ themes: [], total_items_in_window: 0, window_days: d });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        refresh(days);
        // eslint-disable-next-line
    }, [days]);
    const maxCount = Math.max(1, ...(data?.themes || []).map((t) => t.count));
    return (
        <div className="bl-card p-5 md:p-6" data-testid="admin-feedback-top-themes">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-bl-primary" />
                    <h3 className="font-display text-bl text-lg">
                        Top feedback themes
                    </h3>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            type="button"
                            onClick={() => setDays(d)}
                            className={
                                "px-2.5 py-1 rounded-full text-xs " +
                                (d === days
                                    ? "bg-bl-primary text-bl-surface"
                                    : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                            }
                            data-testid={`admin-feedback-top-themes-window-${d}`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div className="mt-4 text-xs text-bl-muted">Loading…</div>
            ) : (data?.themes?.length || 0) === 0 ? (
                <p className="mt-4 text-sm text-bl-muted">
                    No tagged themes in the last {data?.window_days || days} days.
                    Add themes to items below to start surfacing trends here.
                </p>
            ) : (
                <ul className="mt-4 space-y-2" data-testid="admin-feedback-top-themes-list">
                    {data.themes.map((t) => (
                        <li key={t.theme} className="flex items-center gap-3 text-sm">
                            <span className="text-bl flex-1 capitalize">
                                {t.theme.replace(/_/g, " ")}
                            </span>
                            <div className="flex-1 h-2 rounded-full bg-bl-teal-tint/40 overflow-hidden">
                                <div
                                    className="h-full bg-bl-primary rounded-full"
                                    style={{ width: `${Math.round((t.count / maxCount) * 100)}%` }}
                                />
                            </div>
                            <span className="font-mono-bl text-xs text-bl-muted w-6 text-right">
                                {t.count}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {data?.total_items_in_window != null && (
                <p className="mt-3 text-[10px] text-bl-muted">
                    Aggregated across {data.total_items_in_window} feedback items in the
                    last {data.window_days} days.
                </p>
            )}
        </div>
    );
}

export default function AdminFeedback() {
    const [data, setData] = useState({ items: [], stats: {}, themes: [], severities: [], statuses: [] });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ source: "", status: "", rating_min: "", verified_only: false, testimonial_only: false, search: "" });
    const [expandedId, setExpandedId] = useState(null);

    const refresh = async (next) => {
        setLoading(true);
        try {
            const params = {};
            const f = next || filters;
            for (const [k, v] of Object.entries(f)) {
                if (v !== "" && v !== false && v !== undefined) params[k] = v;
            }
            const r = await adminFeedbackList(params);
            setData(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line
    }, []);

    const applyFilter = (patch) => {
        const next = { ...filters, ...patch };
        setFilters(next);
        refresh(next);
    };

    const onPatch = async (item, patch) => {
        try {
            const updated = await adminFeedbackPatch(
                item.source_type,
                item.review_id ||
                    item.feedback_id ||
                    item.issue_feedback_id ||
                    item.exposure_feedback_id ||
                    item.feature_request_id,
                patch,
            );
            setData((d) => ({
                ...d,
                items: d.items.map((x) =>
                    (x.review_id === updated.review_id ||
                        x.feedback_id === updated.feedback_id ||
                        x.issue_feedback_id === updated.issue_feedback_id ||
                        x.exposure_feedback_id === updated.exposure_feedback_id ||
                        x.feature_request_id === updated.feature_request_id)
                        ? { ...x, ...updated }
                        : x,
                ),
            }));
            toast.success("Updated.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update.");
        }
    };

    const onConvertTicket = async (item) => {
        if (!confirm("Create a support ticket from this feedback?")) return;
        const id =
            item.review_id ||
            item.feedback_id ||
            item.issue_feedback_id ||
            item.exposure_feedback_id ||
            item.feature_request_id;
        try {
            const ticket = await adminFeedbackConvertTicket(item.source_type, id);
            toast.success(`Created ticket ${ticket.ticket_id}.`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not convert.");
        }
    };

    return (
        <div data-testid="admin-feedback-panel">
            {/* Aging-feedback triage queue */}
            <FeedbackTriagePanel />
            {/* Top themes (last 7d / 30d / 90d) */}
            <div className="mt-4">
                <TopThemesPanel />
            </div>
            {/* Stats */}
            <div className="mt-4 bl-card p-5 md:p-6 grid grid-cols-2 md:grid-cols-6 gap-4">
                <StatItem label="New reviews" value={data.stats?.new_reviews || 0} tone="text-bl-primary" />
                <StatItem label="Issue flagged" value={data.stats?.issue_flagged || 0} tone="text-[#C85B3A]" />
                <StatItem label="Testimonial candidates" value={data.stats?.testimonial_candidates || 0} tone="text-bl-primary" />
                <StatItem label="New feature requests" value={data.stats?.feature_requests_new || 0} />
                <StatItem label="Exposure too low" value={data.stats?.exposure_too_low || 0} tone="text-emerald-700" />
                <StatItem label="Exposure too high" value={data.stats?.exposure_too_high || 0} tone="text-[#C85B3A]" />
            </div>

            {/* Filters */}
            <div className="mt-4 bl-card p-4 flex flex-wrap items-center gap-3">
                <Filter className="w-4 h-4 text-bl-muted" />
                <select
                    value={filters.source}
                    onChange={(e) => applyFilter({ source: e.target.value })}
                    className="px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="admin-feedback-filter-source"
                >
                    <option value="">All sources</option>
                    {Object.entries(SOURCE_LABEL).map(([id, label]) => (
                        <option key={id} value={id}>
                            {label}
                        </option>
                    ))}
                </select>
                <select
                    value={filters.status}
                    onChange={(e) => applyFilter({ status: e.target.value })}
                    className="px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="admin-feedback-filter-status"
                >
                    <option value="">Any status</option>
                    {(data.statuses || []).map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <select
                    value={filters.rating_min}
                    onChange={(e) => applyFilter({ rating_min: e.target.value })}
                    className="px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                >
                    <option value="">Any rating</option>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>{`≥ ${n}★`}</option>
                    ))}
                </select>
                <label className="inline-flex items-center gap-1.5 text-xs text-bl">
                    <input
                        type="checkbox"
                        checked={filters.verified_only}
                        onChange={(e) => applyFilter({ verified_only: e.target.checked })}
                    />
                    Verified only
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs text-bl">
                    <input
                        type="checkbox"
                        checked={filters.testimonial_only}
                        onChange={(e) => applyFilter({ testimonial_only: e.target.checked })}
                    />
                    Testimonial-permission only
                </label>
                <input
                    type="search"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") refresh();
                    }}
                    placeholder="Search text / email / report id"
                    className="px-3 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm flex-1 min-w-[200px]"
                    data-testid="admin-feedback-search"
                />
            </div>

            {/* List */}
            {loading ? (
                <div className="mt-6 text-sm text-bl-muted">Loading…</div>
            ) : data.items.length === 0 ? (
                <div className="mt-6 bl-card p-8 text-center text-bl-muted">
                    No feedback matches these filters yet.
                </div>
            ) : (
                <ul className="mt-4 space-y-3" data-testid="admin-feedback-list">
                    {(() => {
                        const seen = new Set();
                        return data.items.filter((item) => {
                            const id =
                                item.review_id ||
                                item.feedback_id ||
                                item.issue_feedback_id ||
                                item.exposure_feedback_id ||
                                item.feature_request_id;
                            const key = `${item.source_type}-${id}`;
                            if (seen.has(key)) return false;
                            seen.add(key);
                            return true;
                        });
                    })().map((item) => {
                        const id =
                            item.review_id ||
                            item.feedback_id ||
                            item.issue_feedback_id ||
                            item.exposure_feedback_id ||
                            item.feature_request_id;
                        const expanded = expandedId === id;
                        const text =
                            item.feedback_text ||
                            item.comment ||
                            item.request_text ||
                            (item.feedback_value && `Exposure: ${item.feedback_value}`) ||
                            (item.helpful === false ? "Marked unhelpful" : item.helpful === true ? "Marked helpful" : "(no text)");
                        const rating = item.rating;
                        return (
                            <li
                                key={`${item.source_type}-${id}`}
                                className="bl-card p-4 md:p-5"
                                data-testid={`admin-feedback-item-${id}`}
                            >
                                <div className="flex items-start gap-3 flex-wrap">
                                    <span className="px-2 py-0.5 rounded text-[10px] tracking-[0.15em] uppercase bg-bl-teal-tint/40 text-bl-primary font-medium">
                                        {SOURCE_LABEL[item.source_type] || item.source_type}
                                    </span>
                                    {item.verified_purchase && (
                                        <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.15em] bg-emerald-100 text-emerald-700">
                                            Verified
                                        </span>
                                    )}
                                    {rating != null && (
                                        <span className="inline-flex items-center gap-0.5 text-bl-primary text-xs">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={
                                                        "w-3 h-3 " +
                                                        (i < rating
                                                            ? "fill-bl-primary"
                                                            : "text-bl-muted")
                                                    }
                                                />
                                            ))}
                                        </span>
                                    )}
                                    {item.status && (
                                        <span
                                            className={
                                                "px-2 py-0.5 rounded text-[10px] tracking-[0.15em] uppercase " +
                                                (STATUS_TONE[item.status] || "bg-bl-bg text-bl-muted")
                                            }
                                        >
                                            {item.status.replace(/_/g, " ")}
                                        </span>
                                    )}
                                    {item.testimonial_permission && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.15em] bg-bl-teal-tint text-bl-primary">
                                            <Award className="w-3 h-3" /> Testimonial OK
                                        </span>
                                    )}
                                    <span className="ml-auto text-xs text-bl-muted font-mono-bl">
                                        {new Date(item.created_at).toLocaleString()}
                                    </span>
                                </div>
                                <p className="mt-3 text-sm text-bl whitespace-pre-wrap leading-relaxed">
                                    {text}
                                </p>
                                <div className="mt-3 text-xs text-bl-muted flex flex-wrap gap-3">
                                    {item.email && (
                                        <span>{item.email}</span>
                                    )}
                                    {item.report_id && (
                                        <a
                                            href={`/report/${item.report_id}`}
                                            className="inline-flex items-center gap-1 text-bl-primary hover:underline"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {item.report_id.slice(0, 18)}…{" "}
                                            <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                    {item.issue_id && <span>issue {item.issue_id}</span>}
                                    {item.property_zip && <span>ZIP {item.property_zip}</span>}
                                    {item.severity && (
                                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-wide">
                                            {item.severity}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setExpandedId(expanded ? null : id)}
                                    className="mt-3 text-xs text-bl-primary inline-flex items-center gap-1 hover:underline"
                                    data-testid={`admin-feedback-expand-${id}`}
                                >
                                    {expanded ? "Hide actions" : "Open actions"}
                                    <ChevronDown
                                        className={
                                            "w-3 h-3 transition-transform " +
                                            (expanded ? "rotate-180" : "")
                                        }
                                    />
                                </button>
                                {expanded && (
                                    <div className="mt-3 pt-3 border-t border-bl space-y-3">
                                        <div className="flex flex-wrap gap-2">
                                            {(data.statuses || []).map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => onPatch(item, { status: s })}
                                                    className={
                                                        "px-2.5 py-1 rounded-full text-xs " +
                                                        (item.status === s
                                                            ? "bg-bl-primary text-bl-surface"
                                                            : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                                                    }
                                                    data-testid={`admin-feedback-status-${id}-${s}`}
                                                >
                                                    {s.replace(/_/g, " ")}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {(data.severities || []).map((sv) => (
                                                <button
                                                    key={sv}
                                                    onClick={() => onPatch(item, { severity: sv })}
                                                    className={
                                                        "px-2.5 py-1 rounded-full text-xs " +
                                                        (item.severity === sv
                                                            ? "bg-amber-200 text-amber-900"
                                                            : "bg-bl-bg text-bl-muted hover:bg-bl-teal-tint/30")
                                                    }
                                                >
                                                    {sv}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {(data.themes || []).map((th) => {
                                                const active = (item.themes || []).includes(th);
                                                return (
                                                    <button
                                                        key={th}
                                                        onClick={() => {
                                                            const next = active
                                                                ? (item.themes || []).filter((x) => x !== th)
                                                                : [...(item.themes || []), th];
                                                            onPatch(item, { themes: next });
                                                        }}
                                                        className={
                                                            "px-2 py-0.5 rounded text-[10px] uppercase tracking-wide " +
                                                            (active
                                                                ? "bg-bl-primary text-bl-surface"
                                                                : "bg-bl-bg text-bl-muted hover:bg-bl-teal-tint/30")
                                                        }
                                                    >
                                                        {th.replace(/_/g, " ")}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <textarea
                                            placeholder="Internal admin notes…"
                                            defaultValue={item.admin_notes || ""}
                                            onBlur={(e) => {
                                                if ((e.target.value || "") !== (item.admin_notes || "")) {
                                                    onPatch(item, { admin_notes: e.target.value });
                                                }
                                            }}
                                            rows={2}
                                            className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                                            data-testid={`admin-feedback-notes-${id}`}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => onConvertTicket(item)}
                                                className="bl-btn-secondary text-xs"
                                                data-testid={`admin-feedback-convert-${id}`}
                                            >
                                                <MessageSquareWarning className="w-3.5 h-3.5" />
                                                Create support ticket
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
